import { jsPDF as JsPDF } from 'jspdf';

// Brand purple (matches the panel headings) as RGB for jsPDF.
const PRIMARY: [number, number, number] = [109, 40, 217];
const INK: [number, number, number] = [31, 41, 55];
const MUTED: [number, number, number] = [107, 114, 128];
const RULE: [number, number, number] = [229, 231, 235];
const CALLOUT_BG: [number, number, number] = [245, 243, 255];

// Strip the markdown the briefing uses down to plain text for the PDF.
const clean = (s: string): string => s
  .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // [text](url) -> text
  .replace(/\*\*(.*?)\*\*/g, '$1') // bold
  .replace(/`([^`]*)`/g, '$1') // inline code
  .replace(/^\s*>\s?/, '') // blockquote marker
  .trim();

const rowCells = (row: string): string[] => {
  const parts = row.split('|').map((c) => clean(c));
  if (parts.length && parts[0] === '') parts.shift();
  if (parts.length && parts[parts.length - 1] === '') parts.pop();
  return parts;
};

const isDelimiterRow = (row: string): boolean => /^\s*\|?[\s:|-]+\|?\s*$/.test(row) && row.includes('-');

/**
 * Render the briefing markdown (`reply`) into a downloadable, professionally styled PDF.
 * Self-contained — uses only jsPDF (already a project dependency); no server round-trip.
 */
const downloadBriefingAsPdf = (reply: string, generatedOn?: string): void => {
  const doc = new JsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentW = pageW - margin * 2;
  let y = margin;
  let uid = 'briefing';

  const ensure = (space: number) => {
    if (y + space > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const paragraph = (text: string, opts: { size?: number; color?: [number, number, number]; bold?: boolean; gap?: number; indent?: number } = {}) => {
    const {
      size = 10.5, color = INK, bold = false, gap = 5, indent = 0,
    } = opts;
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, contentW - indent);
    lines.forEach((ln: string) => {
      ensure(size + 2);
      doc.text(ln, margin + indent, y);
      y += size + 2;
    });
    y += gap;
  };

  const table = (rows: string[]) => {
    const dataRows = rows.map(rowCells).filter((r, i) => !(i === 1 && isDelimiterRow(rows[1])));
    if (!dataRows.length) return;
    const cols = dataRows[0].length;
    const colW = contentW / cols;
    const pad = 5;

    dataRows.forEach((cells, ri) => {
      doc.setFont('helvetica', ri === 0 ? 'bold' : 'normal');
      doc.setFontSize(9);
      const wrapped = cells.map((c) => doc.splitTextToSize(c || '–', colW - pad * 2));
      const rowH = Math.max(...wrapped.map((w) => w.length)) * 11 + pad * 2;
      ensure(rowH);
      if (ri === 0) {
        doc.setFillColor(...CALLOUT_BG);
        doc.rect(margin, y, contentW, rowH, 'F');
      }
      doc.setDrawColor(...RULE);
      doc.rect(margin, y, contentW, rowH);
      wrapped.forEach((w, ci) => {
        doc.setTextColor(...(ri === 0 ? PRIMARY : INK));
        doc.text(w, margin + ci * colW + pad, y + pad + 8);
      });
      y += rowH;
    });
    y += 8;
  };

  const lines = reply.split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    const line = raw.trim();

    if (line.startsWith('|')) {
      const block: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        block.push(lines[i].trim());
        i += 1;
      }
      i -= 1;
      table(block);
    } else if (line.startsWith('# ')) {
      const title = clean(line.slice(2));
      const m = title.match(/LMR-\d+/i);
      if (m) [uid] = m;
      paragraph(title, {
        size: 17, color: PRIMARY, bold: true, gap: 8,
      });
    } else if (line.startsWith('## ')) {
      y += 10;
      ensure(22);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...PRIMARY);
      doc.text(clean(line.slice(3)).toUpperCase(), margin, y);
      y += 6;
      doc.setDrawColor(...RULE);
      doc.line(margin, y, margin + contentW, y);
      y += 12;
    } else if (line.startsWith('> ')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quote.push(clean(lines[i]));
        i += 1;
      }
      i -= 1;
      const text = quote.join(' ');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10.5);
      const wrapped = doc.splitTextToSize(text, contentW - 24);
      const boxH = wrapped.length * 13 + 16;
      ensure(boxH);
      doc.setFillColor(...CALLOUT_BG);
      doc.rect(margin, y, contentW, boxH, 'F');
      doc.setFillColor(...PRIMARY);
      doc.rect(margin, y, 3, boxH, 'F');
      doc.setTextColor(76, 29, 149);
      doc.text(wrapped, margin + 12, y + 14);
      y += boxH + 10;
    } else if (/^[-*]\s+/.test(line)) {
      paragraph(`•  ${clean(line.replace(/^[-*]\s+/, ''))}`, { gap: 2, indent: 8 });
    } else if (/^\s+[-*]\s+/.test(raw)) {
      paragraph(`–  ${clean(line.replace(/^[-*]\s+/, ''))}`, { gap: 2, indent: 24, color: MUTED });
    } else if (line) {
      paragraph(clean(line));
    } else {
      y += 3;
    }
  }

  // Footer on every page.
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    const stamp = generatedOn ? `Generated ${generatedOn}` : 'Meeting Briefing';
    doc.text(stamp, margin, pageH - 24);
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 24, { align: 'right' });
  }

  doc.save(`meeting-briefing-${uid}.pdf`);
};

export default downloadBriefingAsPdf;
